// @/components/home/HomeCarouselSection.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import HorizontalCarousel from "@/components/HorizontalCarousel";
import { ReactNode } from "react";
import { CarouselItem } from "@/components/ui/carousel";
import { SiteFrame } from "@/components/layout/SiteFrame";
import { Typography } from "../Typography";

interface HomeCarouselSectionProps<T> {
  title: string;
  description?: string;
  icon?: string;
  href?: string;
  items: T[] | undefined;
  isFetching: boolean;
  isError?: boolean;
  skeletonCount?: number;
  itemsPerCol?: 3 | 4 | "3-grid";
  getKey: (item: T) => string | number;
  renderItem: (item: T) => ReactNode;
  renderSkeleton: () => ReactNode;
}

const WIDTH_CLASS: Record<3 | 4 | "3-grid", string> = {
  4: "w-[80%] sm:w-[45%] md:w-[35%] lg:w-[300px]",
  3: "w-[70%] sm:w-[50%] md:w-[40%] lg:w-[350px]",
  "3-grid": "w-[70%] sm:w-[50%] md:w-[40%] lg:w-[32.5%]",
};

function HomeCarouselSection<T>({
  title,
  description,
  icon,
  href,
  items,
  isFetching,
  isError,
  skeletonCount = 5,
  itemsPerCol = 4,
  getKey,
  renderItem,
  renderSkeleton,
}: HomeCarouselSectionProps<T>) {
  const showSkeleton = (isFetching || items === undefined) && !items?.length;
  const widthClass = WIDTH_CLASS[itemsPerCol];

  return (
    <section className="w-full">
      <SiteFrame>
        <div className="flex items-start justify-between my-2 md:mt-4 mt-2">
          <div className="flex items-center gap-1">
            {icon ? (
              <Image
                src={icon}
                alt={title}
                width={28}
                height={28}
                className="size-8"
              />
            ) : null}
            <div>
              <Typography variant="h6" mdVariant="h4">
                {title}
              </Typography>
              {description ? (
                <Typography variant="body1" className="mt-1 mb-4">
                  {description}
                </Typography>
              ) : null}
            </div>
          </div>

          {href ? (
            <Link href={href}>
              <Typography
                variant="body2"
                className="text-secondary hover:underline mt-1 shrink-0 "
              >
                View All
              </Typography>
            </Link>
          ) : null}
        </div>

        {isError ? (
          <p className="text-sm text-red-500 bg-error-background w-fit p-2 rounded-lg">
            Failed to load {title.toLowerCase()}.
          </p>
        ) : (
          <HorizontalCarousel>
            {showSkeleton
              ? Array.from({ length: skeletonCount }).map((_, i) => (
                  <CarouselItem
                    key={i}
                    className={`basis-auto shrink-0 ${widthClass}`}
                  >
                    {renderSkeleton()}
                  </CarouselItem>
                ))
              : items?.map((item) => (
                  <CarouselItem
                    key={getKey(item)}
                    className={`basis-auto shrink-0 ${widthClass}`}
                  >
                    {renderItem(item)}
                  </CarouselItem>
                ))}
          </HorizontalCarousel>
        )}
      </SiteFrame>
    </section>
  );
}

export default HomeCarouselSection;
